import React from "react";
import { useTranslation } from "react-i18next";
import styled from "@emotion/styled";
import { Box, Typography, useTheme } from "@mui/material";
import { Subscription } from "@rentablo/api/apiv1";
import { goToCashbackDashboard } from "@helpers/mainAppUrls";
import { Card as CardOrig } from "@rentablo/components";
import { CardLabel } from "@rentablo/components";
import { CashbackNotificationStatusMessage } from "./CashbackNotificationStatusMessage";
import { calculateCurrentCashbackStatus } from "./utils/calculateCurrentCashbackStatus";

const Card = styled(CardOrig, {
  shouldForwardProp: (prop) => prop !== "isDark" && prop !== "border",
})<{ isDark?: boolean; height?: number | string; border: boolean }>`
  padding: ${({ theme }) => theme.spacing(4.75, 4, 3)};
  box-sizing: border-box;
  height: ${({ height }) =>
    typeof height === "string" ? height : height ? `${height}px` : "192px"};
  overflow: hidden;
  background-color: ${({ isDark, theme }) =>
    isDark ? theme.palette.background.subtle : theme.palette.background.paper};
  ${({ border }) => !border && "border: none"};
`;

export interface CashbackNotificationStatusProps {
  subscriptions: Subscription[];
  height?: number | string;
}

const NoSubscriptions = () => {
  const { t } = useTranslation();
  const theme = useTheme();

  return (
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="space-between"
      height="100%"
    >
      <CardLabel
        title={t("dashboardPage.cashbackCard.noSubscriptions.title")}
        sx={{ "& .MuiTypography-root": { color: theme.palette.success.dark } }}
      />
      <Box>
        <Typography
          variant="body1"
          color="textPrimary"
          sx={{
            fontSize: "16px",
            lineHeight: "24px",
            fontWeight: 600,
          }}
        >
          {t("dashboardPage.cashbackCard.noSubscriptions.subtitle")}
        </Typography>
        <Typography
          variant="body2"
          color="textSecondary"
          sx={{ fontWeigth: 500 }}
        >
          {t("dashboardPage.cashbackCard.noSubscriptions.text")}
        </Typography>
      </Box>
    </Box>
  );
};

const WithSubscriptions = ({
  subscriptions,
}: CashbackNotificationStatusProps) => {
  const { t } = useTranslation();
  const currentStatus = calculateCurrentCashbackStatus(subscriptions);
  const isBeingProcessed = [
    "internal_processing",
    "processing_at_bank",
    "processing_at_bank_reduced",
  ].includes(currentStatus);
  const isActive = currentStatus === "processed";
  const isCanceled = currentStatus === "contract_terminated";

  return (
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="space-between"
      height="100%"
    >
      <CardLabel
        title={t("dashboardPage.cashbackCard.withSubscriptions.title")}
        firstLineMargin={0}
      />

      {!isCanceled && (
        <Typography variant="body3" color="textSecondary">
          {t(
            `dashboardPage.cashbackCard.withSubscriptions.${
              isBeingProcessed
                ? "text_processed"
                : isActive
                  ? "text_active"
                  : "text"
            }`
          )}
        </Typography>
      )}

      <CashbackNotificationStatusMessage status={currentStatus} />
    </Box>
  );
};

export const CashbackNotificationStatus = ({
  subscriptions,
  height,
}: CashbackNotificationStatusProps) => {
  const handlerClick = () => {
    goToCashbackDashboard();
  };

  return (
    <Card onClick={handlerClick} height={height} border={false} isDark>
      {subscriptions?.length > 0 ? (
        <WithSubscriptions subscriptions={subscriptions} />
      ) : (
        <NoSubscriptions />
      )}
    </Card>
  );
};
